import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { useFormikWrapper } from "../../../components/formik_wrapper";

import * as actions from "../../../actions";
import { ADMIN, SUPERADMIN } from "../../../constants/roles";
import { ZuperProV2URL, ZuperProV3URL } from "../../../util/master-data";

import { Popup } from "../../../components/pop-up";
import { FlatButton, IconButton } from "../../../components/buttons";
import { ACL } from "../../../components/acl";
import FieldRenderer from "../../../components/policy_field_renderer";

function CompanyPolicy({ company_uid, companyDetails }) {
  const dispatch = useDispatch();

  const currentPolicy = useSelector((state) => state.company.policy);
  const companyPolicyFields = useSelector((state) => state.company.policyFields) || [];
  const company_modules = useSelector((state) => state.company.modules);

  const policyDetailsForm = useFormikWrapper({
    initialValues: {},
    onSubmit: () => {
      setOpenModal("pop-up");
    },
  });

  const initForm = () => {
    policyDetailsForm.setValues(currentPolicy || {});
  };

  const [openModal, setOpenModal] = useState();
  const [editMode, setEditMode] = useState(false);

  useEffect(() => {
    dispatch(actions.GET_COMPANY_POLICY_FIELDS_REQ())
    dispatch(actions.GETCOMPANYPOLICY_REQ({ company_uid }));
    if (company_uid) dispatch(actions.GETMODULES_REQ({ company_uid }));
  }, [company_uid]);


  useEffect(() => {
    // Change the v3 domain url based on v3 toggle when edit mode is on
    if (editMode) {
      if (policyDetailsForm.values?.is_v3_enabled) {
        policyDetailsForm.setFieldValue("custom_domain", ZuperProV3URL);
      } else {
        policyDetailsForm.setFieldValue("custom_domain", ZuperProV2URL);
      }
    }
  }, [policyDetailsForm.values?.is_v3_enabled]);

  useEffect(() => {
    setEditMode(false);
    initForm();
  }, [currentPolicy]);

  const handleEdit = () => {
    setOpenModal(undefined);
    window.history.replaceState(null, null, window.location.pathname);
    dispatch(
      actions.EDITCOMPANYPOLICY_REQ({
        company_uid,
        formData: policyDetailsForm.values,
      })
    );
  };
  let timerRef = useRef();

  useEffect(() => {
    if (window.location.hash) {
      setEditMode(true);
      const elementId = window.location.hash.split("#")[1];
      const element = document.getElementById(elementId);

      if (element) {
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
          element.scrollIntoView({
            block: "center",
            behavior: "smooth",
            inline: "center",
          });

          // Use requestAnimationFrame to ensure focus happens after scroll
          requestAnimationFrame(() => {
            setTimeout(() => {
              element.animate(
                [
                  { background: "#3f83f82F", offset: 0 },
                  { background: "#3f83f800", offset: 0.25 },
                  { background: "#3f83f82F", offset: 0.5 },
                  { background: "#3f83f800", offset: 0.75 },
                  // { background: "#3f83f82F", offset: 1 },
                ],
                {
                  duration: 1000,
                  // delay: 100,
                  easing: "ease-in",
                }
              );
              element.getElementsByTagName("input")?.[1]?.focus();
            }, 300); // Adjust the delay if needed to match the scroll duration
          });
        },300);
      }
    }
  }, [currentPolicy]);

  return (
    <>
      <div class="h-full mt-3">
        <div class="relative h-full w-full bg-white rounded-lg shadow">
          <div class="flow-root">
            <div class={`mx-auto pb-3 border-b sm:flex sm:items-center sm:justify-between ${
                (editMode && "sticky top-0") || ""
              } py-4 bg-white z-10 sm:px-6 lg:px-8 shadow`}  rounded-t-lg
            >
              <h3 class="text-lg font-semibold leading-6 text-gray-900">
                Company Policy
              </h3>
              <ACL showFor={[SUPERADMIN, ADMIN]}>
                <div class="mt-3 sm:ml-4 sm:mt-0">
                  {!editMode && (
                    <div class="flex items-center justify-center space-x-3">
                      <IconButton onClick={() => setEditMode(true)}>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          class="w-4 h-4"
                        >
                          <path d="M21.731 2.269a2.625 2.625 0 00-3.712 0l-1.157 1.157 3.712 3.712 1.157-1.157a2.625 2.625 0 000-3.712zM19.513 8.199l-3.712-3.712-12.15 12.15a5.25 5.25 0 00-1.32 2.214l-.8 2.685a.75.75 0 00.933.933l2.685-.8a5.25 5.25 0 002.214-1.32L19.513 8.2z" />
                        </svg>
                      </IconButton>
                    </div>
                  )}
                  {editMode && (
                    <div class="flex items-center justify-center space-x-3">
                      <>
                        <FlatButton
                          appearance="secondary"
                          onClick={() => {
                            initForm();
                            setEditMode(false);
                          }}
                          className="w-[100px] inline-flex justify-center items-center gap-x-1.5"
                        >
                          Cancel
                        </FlatButton>
                        <FlatButton
                          className="w-[100px] inline-flex justify-center items-center gap-x-1.5"
                          onClick={() => {
                            policyDetailsForm.submitForm();
                          }}
                        >
                          Save
                        </FlatButton>
                      </>
                    </div>
                  )}
                </div>
              </ACL>
            </div>
            <div class="inline-block min-w-full py-4 align-middle sm:px-6 lg:px-8">
              <div class="relative mt-0">
                {!editMode && (
                  <div className="absolute w-full h-full z-30 top-0 left-0"></div>
                )}

                {companyPolicyFields && companyPolicyFields?.map((field, index) => (
                  <FieldRenderer
                    key={index}
                    field={field}
                    form={policyDetailsForm}
                    parentKey={null}
                    readOnly={!editMode}
                    currentPolicy={currentPolicy}
                    company_modules={company_modules?.data}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
        <Popup
          modalState={[openModal, setOpenModal]}
          onConfirm={handleEdit}
          content={
            <>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="mx-auto mb-4 h-14 w-14 text-gray-400 dark:text-gray-200"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
                />
              </svg>
              <h3 className="mb-5 text-lg font-normal text-gray-500 dark:text-gray-400">
                Are you sure you want to edit the policy of{" "}
                <b>{companyDetails?.company_name}</b>?
              </h3>
            </>
          }
        />
      </div>
    </>
  );
}

export default CompanyPolicy;
